import React, { useState, useEffect } from 'react';
import { Usuario, PerfilPermissao } from '../../../../types/admin';
import { apiService } from '../../../../services/api';
import { modalUtils } from '../../../../utils/modalUtils';

interface Props {
  usuario: Usuario;
  onClose: () => void;
  onRefresh: () => void;
}

export const ModalAlterarPerfil: React.FC<Props> = ({ usuario, onClose, onRefresh }) => {
  const [perfis, setPerfis] = useState<PerfilPermissao[]>([]);
  const [perfilSelecionado, setPerfilSelecionado] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingPerfis, setLoadingPerfis] = useState(false);
  const [perfilAtual, setPerfilAtual] = useState<any>(null);

  // Carregar perfis disponíveis
  const fetchPerfis = async () => {
    try {
      setLoadingPerfis(true);
      const response = await apiService.getPerfisPermissao({});
      setPerfis(response.data);
    } catch (error) {
      console.error('Erro ao buscar perfis:', error);
    } finally {
      setLoadingPerfis(false);
    }
  };

  // Carregar perfil atual do usuário
  const fetchPerfilAtual = async () => {
    try {
      const response = await apiService.getPerfilUsuario(usuario.id);
      setPerfilAtual(response.data);
      if (response.data?.id_perfil_base) {
        setPerfilSelecionado(response.data.id_perfil_base);
      }
    } catch (error) {
      console.error('Erro ao buscar perfil atual:', error);
    }
  };

  // Alterar perfil do usuário
  const handleAlterarPerfil = async () => {
    if (!perfilSelecionado) {
      window.alert('Por favor, selecione um perfil.');
      return;
    }

    if (!window.confirm('Tem certeza que deseja alterar o perfil deste usuário? Todas as permissões serão baseadas no novo perfil.')) {
      return;
    }

    try {
      setLoading(true);
      
      // Sempre usar updatePerfilUsuario
      await apiService.updatePerfilUsuario({
        id_usuario: usuario.id,
        id_perfil: perfilSelecionado
      });

      window.alert('Perfil alterado com sucesso!');
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Erro ao alterar perfil:', error);
      window.alert('Erro ao alterar perfil. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPerfis();
    fetchPerfilAtual();
  }, [usuario.id]);

  return (
    <div className="modal fade" id="modal_alterar_perfil" tabIndex={-1} role="dialog" aria-hidden="true">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header border-bottom">
            <h5 className="modal-title">
              🔄 Alterar Perfil - {usuario.nome} {usuario.sobrenome}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="modal-body">
            {/* Perfil Atual */}
            {perfilAtual && (
              <div className="alert alert-info mb-4">
                <div className="d-flex align-items-center">
                  <i className="ti ti-info-circle me-2 fs-4"></i>
                  <div>
                    <strong>Perfil Atual:</strong>
                    <br />
                    <span 
                      className="badge fs-6 px-3 py-2 me-2"
                      style={{ 
                        backgroundColor: perfilAtual.perfil_cor || '#6B7280',
                        color: '#fff'
                      }}
                    >
                      {perfilAtual.perfil_nome}
                    </span>
                    <small className="text-muted">
                      {perfilAtual.perfil_descricao || 'Sem descrição'}
                    </small>
                  </div>
                </div>
              </div>
            )}

            {/* Seleção de Novo Perfil */}
            <div className="row">
              <div className="col-12">
                <label className="form-label">
                  <i className="ti ti-user-cog me-2"></i>
                  Selecionar Novo Perfil
                </label>
                
                {loadingPerfis ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm" role="status"></div>
                    <span className="ms-2">Carregando perfis...</span>
                  </div>
                ) : (
                  <div className="perfis-grid">
                    {perfis.map(perfil => (
                      <div 
                        key={perfil.id}
                        className={`perfil-card ${perfilSelecionado === perfil.id ? 'selected' : ''}`}
                        onClick={() => setPerfilSelecionado(perfil.id)}
                        style={{
                          border: '2px solid',
                          borderColor: perfilSelecionado === perfil.id ? perfil.cor || '#007bff' : '#e0e0e0',
                          borderRadius: '8px',
                          padding: '16px',
                          margin: '8px 0',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          backgroundColor: perfilSelecionado === perfil.id ? (perfil.cor || '#007bff') + '10' : 'transparent'
                        }}
                      >
                        <div className="d-flex align-items-start">
                          <div className="form-check me-3">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="perfil"
                              checked={perfilSelecionado === perfil.id}
                              onChange={() => setPerfilSelecionado(perfil.id)}
                            />
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2">
                              <span 
                                className="badge fs-6 px-3 py-2 me-2"
                                style={{ 
                                  backgroundColor: perfil.cor || '#6B7280',
                                  color: '#fff'
                                }}
                              >
                                {perfil.nome}
                              </span>
                              {perfil.ativo ? (
                                <span className="badge bg-success-light text-success">Ativo</span>
                              ) : (
                                <span className="badge bg-danger-light text-danger">Inativo</span>
                              )}
                            </div>
                            <p className="text-muted mb-2">
                              {perfil.descricao || 'Sem descrição disponível'}
                            </p>
                            <div className="row text-center">
                              <div className="col-4">
                                <small className="text-muted d-block">Módulos</small>
                                <strong className="text-primary">{perfil.total_modulos || 0}</strong>
                              </div>
                              <div className="col-4">
                                <small className="text-muted d-block">Ações</small>
                                <strong className="text-info">{perfil.total_acoes || 0}</strong>
                              </div>
                              <div className="col-4">
                                <small className="text-muted d-block">Usuários</small>
                                <strong className="text-success">{perfil.total_usuarios || 0}</strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {perfis.length === 0 && !loadingPerfis && (
                  <div className="text-center py-4">
                    <div className="avatar avatar-lg bg-warning-light mx-auto mb-3">
                      <i className="ti ti-alert-triangle fs-2 text-warning"></i>
                    </div>
                    <h6>Nenhum perfil disponível</h6>
                    <p className="text-muted">Não há perfis cadastrados no sistema.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Aviso sobre exceções */}
            {perfilSelecionado && (
              <div className="alert alert-warning mt-3">
                <i className="ti ti-alert-triangle me-2"></i>
                <strong>Atenção:</strong> Ao alterar o perfil, o usuário herdará todas as permissões do novo perfil. 
                As exceções específicas (se houver) serão mantidas e aplicadas sobre o novo perfil.
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAlterarPerfil}
              disabled={loading || !perfilSelecionado}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Alterando...
                </>
              ) : (
                <>
                  <i className="ti ti-check me-2"></i>
                  Alterar Perfil
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};